import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";

/**
 * Hero móvil ULTRA optimizado para rendimiento
 * - SIN video (ahorra datos y batería)
 * - SIN animaciones (reduce TBT)
 * - Dimensiones fijas para evitar CLS
 * - Imagen con eager loading para LCP
 */
const MobileHero: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const langPrefix = `/${i18n.language}`;

  return (
    <section
      className="relative w-full bg-transparent overflow-hidden flex items-center pt-20 pb-8"
      style={{ minHeight: "100svh" }}
    >
      {/* Background simple - sin video, sin blur */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          background:
            "linear-gradient(135deg, rgba(0,233,190,0.05) 0%, rgba(0,52,45,0.05) 100%)",
        }}
      />

      <div className="container mx-auto px-4 relative z-10 flex flex-col gap-8">
        {/* Content - Text */}
        <div className="w-full text-left">
          <h1
            className="text-4xl font-bold tracking-tight text-[#00342d] mb-4 leading-[1.1]"
            style={{ fontFamily: "'Bruno Ace', sans-serif" }}
          >
            {t("seo.home.h1")
              .split(" ")
              .map((word, i) => (
                <span
                  key={i}
                  className={
                    word.toUpperCase().includes("VETT") ||
                    word.toUpperCase().includes("CONSULT")
                      ? "text-[#00e9be]"
                      : ""
                  }
                >
                  {word}{" "}
                </span>
              ))}
          </h1>

          <p className="text-base text-gray-600 mb-6 max-w-lg leading-relaxed">
            {t("home.hero.subtitle")}
          </p>

          <Button
            className="text-base px-8 py-3 font-bold tracking-wide shadow-lg text-[#00342d]"
            onClick={() => navigate(`${langPrefix}/signup`)}
          >
            {t("home.hero.primaryButton")}
          </Button>
        </div>

        {/* Visual - Imagen optimizada con dimensiones fijas */}
        <div
          className="w-full relative flex justify-center"
          style={{ minHeight: "280px" }} // Reservar espacio para evitar CLS
        >
          <div
            className="relative w-full max-w-xs"
            style={{ aspectRatio: "1/1" }}
          >
            {/* Imagen principal - usar SVG que es mucho más liviano */}
            <img
              src="/images/home/hero/VettConsultMockup.svg"
              alt="VettConsult Dashboard"
              width={320}
              height={320}
              className="w-full h-full object-contain"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />

            {/* Floating Elements - Posiciones fijas */}
            <div
              className="absolute -right-1 top-12 px-2 py-1.5 bg-white rounded-lg shadow-sm border border-gray-100 flex items-center gap-1.5"
              style={{ width: "110px", height: "28px" }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-[#00e9be]"></div>
              <span className="text-[9px] font-semibold text-gray-600 truncate">
                {t("dashboard:soapNoteSaved")}
              </span>
            </div>

            <div
              className="absolute -left-1 bottom-16 px-2 py-1.5 bg-[#00342d] rounded-lg shadow-md flex items-center gap-1.5"
              style={{ width: "120px", height: "28px" }}
            >
              <div className="w-3 h-3 rounded-full border border-[#00e9be] flex items-center justify-center">
                <div className="w-1 h-1 bg-[#00e9be] rounded-full"></div>
              </div>
              <span className="text-[9px] font-medium text-white truncate">
                {t("dashboard:analysisReady")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileHero;
